import React, { useMemo, useState } from 'react';

interface SLAAlertBannerProps {
  customers: any[];
  departmentStep?: string; // If provided, only shows alerts for this specific step
  slaDays?: number; // Default 3 days
}

const getDaysAtCurrentStep = (customer: any) => {
  const steps = customer.crm_workflow_steps || customer.workflow_steps || [];
  if (steps.length === 0) return { days: 0, stepName: null };
  
  // Find current active step index (first step that is not Done)
  const currentStepIndex = steps.findIndex((s: any) => s.status !== 'Done');
  if (currentStepIndex === -1) return { days: 0, stepName: null }; // Finished

  const currentStep = steps[currentStepIndex];
  
  let arrivalTime = new Date(customer.created_at).getTime();
  
  // If not the very first step, arrival time is when the previous step was updated
  if (currentStepIndex > 0) {
    const prevStep = steps[currentStepIndex - 1];
    if (prevStep.updated_at) {
      arrivalTime = new Date(prevStep.updated_at).getTime();
    }
  }

  const days = Math.floor((Date.now() - arrivalTime) / (1000 * 60 * 60 * 24));
  return { days, stepName: currentStep.step_name };
};

const SLAAlertBanner: React.FC<SLAAlertBannerProps> = ({ customers, departmentStep, slaDays = 3 }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const alerts = useMemo(() => {
    const issues: { customerName: string; days: number; stepName: string }[] = [];
    
    customers.forEach(cust => {
      const { days, stepName } = getDaysAtCurrentStep(cust);
      
      if (days >= slaDays && stepName) {
        // If viewing as department, only alert if stuck on THEIR step
        if (departmentStep && stepName !== departmentStep) return;
        
        issues.push({ customerName: cust.customer_name || 'Unknown', days, stepName });
      }
    });

    return issues.sort((a, b) => b.days - a.days);
  }, [customers, departmentStep, slaDays]);

  if (alerts.length === 0) return null;

  return (
    <div style={{
      background: '#FEF2F2',
      border: '1px solid #F87171',
      borderLeft: '4px solid #DC2626',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      color: '#991B1B',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <h3 
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ 
          margin: 0, 
          fontSize: '15px', 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '18px' }}>⚠️</span> 
        <span style={{ flex: 1 }}>SLA Violations ({alerts.length} Urgent Actions)</span>
        <span style={{ 
          fontSize: '12px', 
          transition: 'transform 0.2s', 
          transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          display: 'inline-block' 
        }}>
          ▼
        </span>
      </h3>
      
      {!isCollapsed && (
        <ul style={{ margin: '12px 0 0 0', paddingLeft: '24px', fontSize: '14px', lineHeight: '1.6' }}>
          {alerts.map((alert, i) => (
            <li key={i} style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#7F1D1D' }}>{alert.customerName}</strong> has been stuck at <strong style={{color: '#7F1D1D'}}>{alert.stepName}</strong> for <strong style={{ color: '#DC2626', background: '#FEE2E2', padding: '2px 6px', borderRadius: '4px' }}>{alert.days} days</strong>.
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SLAAlertBanner;
